/**
 * VacationDialog — Urlaubsantrag erstellen/eintragen
 */
import React from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, UserPlus, User, ChevronRight, AlertCircle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { vacationsAPI, employeesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

const vacationTypes = ['Urlaub', 'Sonderurlaub', 'Bildungsurlaub', 'Unbezahlter Urlaub'];

const PreviewBox = ({ preview }) => {
  if (!preview) return null;
  return (
    <div className="mt-2 space-y-2" data-testid="vacation-preview">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Urlaubstage:</span>
        <span className="text-lg font-bold text-primary">{preview.business_days}</span>
      </div>
    </div>
  );
};

export default function VacationDialog({
  open, onOpenChange,
  canManageOthers, employees,
  formData, setFormData,
  selectedRange, setSelectedRange,
  preview, saving, onSubmit,
  holidays = [], companyFreeDays = [], schulferien = [],
}) {
  // Build modifiers for react-day-picker: feiertag (Date[]), ferien (Date[])
  const feiertagDates = React.useMemo(() => {
    const all = [...holidays, ...companyFreeDays];
    return all.map(h => parseISO(h.date)).filter(d => !isNaN(d.getTime()));
  }, [holidays, companyFreeDays]);

  const ferienDates = React.useMemo(() => {
    const dates = [];
    schulferien.forEach(f => {
      try {
        const start = parseISO(f.start_date);
        const end = parseISO(f.end_date);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }
      } catch {
        /* ignore parse errors */
      }
    });
    return dates;
  }, [schulferien]);

  const empDupKeys = React.useMemo(() => getDuplicateNameKeys(employees), [employees]);

  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canManageOrg = user?.role === 'admin' || (hasPermission && hasPermission('settings', 'edit'));

  // Vorschau der Genehmigungskette (nur Mitarbeiter-Selbstanträge).
  const [chainPreview, setChainPreview] = React.useState(null);
  const [chainLoading, setChainLoading] = React.useState(false);
  React.useEffect(() => {
    if (!open || canManageOthers) { setChainPreview(null); return; }
    let cancelled = false;
    (async () => {
      setChainLoading(true);
      try {
        const me = await employeesAPI.getMyProfile();
        const empId = me.data?.id;
        if (!empId) { if (!cancelled) setChainPreview(null); return; }
        const res = await vacationsAPI.getEmployeeApprovalChain(empId);
        if (!cancelled) setChainPreview(res.data);
      } catch {
        if (!cancelled) setChainPreview(null);
      } finally {
        if (!cancelled) setChainLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, canManageOthers]);

  const dayModifiers = { feiertag: feiertagDates, ferien: ferienDates };
  const dayModifiersClassNames = {
    feiertag: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200 font-semibold',
    ferien: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] sm:w-full max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {canManageOthers ? 'Urlaub eintragen' : 'Urlaub beantragen'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Zeitraum, Urlaubsart und optionale Notizen für den Urlaubsantrag auswählen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 sm:space-y-6 pt-2 sm:pt-4">
          {canManageOthers && (
            <div className="space-y-2 p-4 bg-secondary/30 rounded-lg border border-border/40">
              <Label className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-primary" />
                Für Mitarbeiter eintragen
              </Label>
              <Select
                value={formData.employee_id || 'self'}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, employee_id: v === 'self' ? '' : v }))}
              >
                <SelectTrigger data-testid="employee-select">
                  <SelectValue placeholder="Mitarbeiter auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Für mich selbst
                    </div>
                  </SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {formatLastFirst(emp, empDupKeys)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.employee_id && (
                <p className="text-xs text-muted-foreground">
                  Der Urlaub wird direkt als "genehmigt" eingetragen.
                </p>
              )}
            </div>
          )}

          {!canManageOthers && (
            <div className="rounded-lg bg-muted/50 p-3 space-y-2" data-testid="vacation-approval-preview">
              <p className="text-xs font-medium text-muted-foreground">Dein Antrag geht an</p>
              {chainLoading ? (
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Genehmiger werden ermittelt…
                </p>
              ) : chainPreview?.chain?.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {chainPreview.chain.map((step, i) => (
                    <React.Fragment key={`${step.label}-${i}`}>
                      {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
                      {(step.approver_details || []).length > 0 ? (
                        (step.approver_details || []).map((a, j) => (
                          <Badge key={`${a.id || a.name}-${j}`} variant="secondary" className="text-xs" data-testid={`vacation-approval-step-${i}`}>
                            {a.name}<span className="opacity-60 ml-1">· {step.label}</span>
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="destructive" className="text-xs" data-testid={`vacation-approval-step-${i}`}>
                          Nicht zugewiesen<span className="opacity-80 ml-1">· {step.label}</span>
                        </Badge>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Wird direkt genehmigt (keine Freigabe nötig).</p>
              )}
              {chainPreview?.chain?.some((s) => !(s.approver_details || []).length) && (
                <div className="space-y-1" data-testid="vacation-approval-warning">
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    Für mindestens eine Stufe ist kein Vorgesetzter hinterlegt. Bitte an HR/Admin wenden.
                  </p>
                  {canManageOrg && (
                    <button
                      type="button"
                      onClick={() => { onOpenChange(false); navigate('/organization'); }}
                      className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                      data-testid="vacation-approval-fix-link"
                    >
                      Standort-/Niederlassungsleiter verwalten
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Zeitraum {selectedRange.from && selectedRange.to ? '' : 'auswählen'}</Label>
            {selectedRange.from && selectedRange.to ? (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium text-sm sm:text-base">
                    {format(selectedRange.from, 'dd.MM.yyyy', { locale: de })} - {format(selectedRange.to, 'dd.MM.yyyy', { locale: de })}
                  </p>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setSelectedRange({ from: null, to: null })}
                    className="text-xs"
                  >
                    Ändern
                  </Button>
                </div>
                <PreviewBox preview={preview} />
              </div>
            ) : (
              <>
                <div className="flex justify-center overflow-x-auto">
                  <Calendar
                    mode="range"
                    selected={selectedRange}
                    onSelect={setSelectedRange}
                    locale={de}
                    className="rounded-md border"
                    data-testid="vacation-calendar"
                    fromDate={new Date(new Date().getFullYear() - 1, 0, 1)}
                    toDate={(() => {
                      const now = new Date();
                      const maxYear = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
                      return new Date(maxYear, 11, 31);
                    })()}
                    modifiers={dayModifiers}
                    modifiersClassNames={dayModifiersClassNames}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pt-1" data-testid="dialog-calendar-legend">
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded bg-purple-100 dark:bg-purple-900/40 border border-purple-300/50" />
                    Feiertag
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded bg-amber-100 dark:bg-amber-900/30 border border-amber-300/50" />
                    Schulferien
                  </div>
                </div>
                {selectedRange.from && selectedRange.to && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {format(selectedRange.from, 'dd.MM.yyyy', { locale: de })} - {format(selectedRange.to, 'dd.MM.yyyy', { locale: de })}
                    </p>
                    {preview && (
                      <div className="p-3 rounded-lg bg-secondary/40 border border-border/40 space-y-2" data-testid="vacation-preview">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Urlaubstage:</span>
                          <span className="text-lg font-bold text-primary">{preview.business_days}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="vacation_type">Urlaubsart</Label>
            <Select
              value={formData.vacation_type}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, vacation_type: v }))}
            >
              <SelectTrigger data-testid="vacation-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {vacationTypes.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notizen (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Zusätzliche Informationen..."
              rows={3}
              data-testid="vacation-notes"
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full w-full sm:w-auto">
              Abbrechen
            </Button>
            <Button
              onClick={onSubmit}
              disabled={saving || !selectedRange.from || !selectedRange.to}
              className="rounded-full w-full sm:w-auto"
              data-testid="submit-vacation-btn"
            >
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird eingereicht...</> : (canManageOthers ? 'Eintragen' : 'Beantragen')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
